import * as React from 'react'

import { cn } from '@/lib/utils'
import styles from './Loader.module.scss'

export type LoaderVariant = 'spinner' | 'bars' | 'dots' | 'blocks'
export type LoaderSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const variantClass: Record<LoaderVariant, string> = {
  spinner: styles.spinner,
  'bars': styles.bars,
  'dots': styles.dots,
  'blocks': styles.blocks,
}

const sizeClass: Record<LoaderSize, string> = {
  xs: styles.xs,
  sm: styles.sm,
  md: styles.md,
  lg: styles.lg,
  xl: styles.xl,
}

const unitCount: Record<LoaderVariant, number> = {
  spinner: 0,
  'bars': 5,
  'dots': 4,
  'blocks': 6,
}

type LoaderStyle = React.CSSProperties & {
  '--loader-size'?: string
}

export type LoaderProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  variant?: LoaderVariant
  size?: LoaderSize | number | string
  label?: string
  showLabel?: boolean
}

function getSizeClass(size: LoaderProps['size']) {
  return typeof size === 'string' && size in sizeClass ? sizeClass[size as LoaderSize] : undefined
}

function getSizeStyle(size: LoaderProps['size'], style: React.CSSProperties | undefined): LoaderStyle | undefined {
  if (size === undefined || (typeof size === 'string' && size in sizeClass)) {
    return style
  }

  const customSize = typeof size === 'number' ? `${size}px` : size
  return {
    ...style,
    '--loader-size': customSize,
  }
}

function Loader({
  className,
  variant = 'spinner',
  size = 'md',
  label = 'Caricamento',
  showLabel = true,
  style,
  ...props
}: LoaderProps) {
  const units = unitCount[variant]

  return (
    <div
      role="status"
      aria-label={label}
      aria-live="polite"
      data-slot="loader"
      data-variant={variant}
      data-size={size}
      className={cn(styles.loader, variantClass[variant], getSizeClass(size), className)}
      style={getSizeStyle(size, style)}
      {...props}
    >
      <span className={styles.mark} aria-hidden="true">
        {Array.from({ length: units }, (_, index) => (
          <span className={styles.unit} key={index} />
        ))}
      </span>
      <span className={cn(styles.label, !showLabel && styles.visuallyHidden)} data-visually-hidden={!showLabel}>
        {label}
      </span>
    </div>
  )
}

export { Loader }
